<template>
  <div id="q-app">
    <router-view />
  </div>
</template>

<script>
import eventBus from 'src/eventBus'
import { NotificationService } from 'src/services/NotificationService'
import { computeLocale } from 'src/utils/computeLocale'
import { getCurrentPosition } from 'src/utils/getCurrentPosition'

export default {
  inject: ['$DB', '$patrimoine', '$cron', '$msal'],
  async created() {
    const lang = (await computeLocale()).i18n
    this.$i18n.locale = lang
    const { Network } = await import('Capacitor/@capacitor/network')
    if (this.$q.platform.is.capacitor) {
      const { App: CapacitorApp } = await import('Capacitor/@capacitor/app')

      CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        if (this.openModal) {
          this.openModal = false
          eventBus.$emit('closeIssueModal')
        } else if (!canGoBack || window.location.hash === '#/categories') {
          CapacitorApp.exitApp()
        } else {
          window.history.back()
        }
      })

      CapacitorApp.addListener('appStateChange', (state) => {
        if (state.isActive) {
          this.emitUpdatePosition()
        }
      })
    }
    this.$DB.initLocal(this.$q.platform.is.capacitor)
    this.$patrimoine.initLocal(this.$q.platform.is.capacitor)
    eventBus.$on('processQueue', this.processQueue) // APP:startSync called or issue closed on device or talk added on device or tmpIssue added
    eventBus.$on('pauseQueue', this.pauseQueue) // User logged out or device reset
    eventBus.$on('userConnected', this.startSync) // User logged in
    eventBus.$on('localIssueChange', this.loadNotifications)
    eventBus.$on('logout', this.logout)
    eventBus.$on('reset', this.reset)
    eventBus.$on('modalOpened', () => {
      this.openModal = true
    })

    if (this.$q.platform.is.capacitor) {
      await this.initOneSignal()
      this.$cron.start('getAppUpdatesJob')
      this.$SowellProxy.isOnMobile = true
      this.isOnline = (await Network.getStatus()).connected
      Network.addListener('networkStatusChange', (status) => {
        this.isOnline = status.connected
      })
      const { Keyboard } = await import('Capacitor/@capacitor/keyboard')
      Keyboard.addListener('keyboardWillShow', async (info) => {
        eventBus.$emit('keyboardWillShow', info)
      })

      Keyboard.addListener('keyboardDidShow', async (info) => {
        eventBus.$emit('keyboardDidShow', info)
      })

      Keyboard.addListener('keyboardWillHide', () => {
        eventBus.$emit('keyboardWillHide')
      })

      Keyboard.addListener('keyboardDidHide', () => {
        eventBus.$emit('keyboardDidHide')
      })
    } else {
      this.isOnline = navigator.onLine
      window.addEventListener('offline', this.handleOfflineEvent)
      window.addEventListener('online', this.handleOnlineEvent)
    }
    await this.getGeolocation()
    eventBus.$on('updatePosition', this.updateGeolocation)
  },
  watch: {
    '$q.appVisible'(val) {
      if (!val) {
        this.pauseQueue()
      }
      if (val && this.isOnline) {
        this.startSync()
      }
    },
    loggedOut(val) {
      if (this.changesListener && val) {
        this.changesListener.cancel()
      }
    },
    isOnline(val) {
      console.info('Network status changed, connected:', val)
      const user = this.$store.getters['reporter/attributes']
      if (val && user.id) {
        this.$DB.replicateIssues(this.$store, user.scopes, user.id)
        this.startSync()
      } else {
        this.$cron.stop('syncTmpItemsJob')
        this.$DB.stopReplication()
      }
    }
  },
  data() {
    return {
      job: null,
      notificationsLoading: false,
      openModal: false,
      changesListener: null,
      loggedOut: false,
      isOnline: false,
      position: {
        longitude: null,
        latitude: null
      }
    }
  },
  computed: {},
  methods: {
    async logout() {
      this.pauseQueue()
      const provider = this.$q.localStorage.getItem('credentials')?.provider
      await this.$store.dispatch('reporter/logout')
      if (provider === 'MSAL') {
        try {
          await this.$msal.signOut()
        } catch {}
      }

      this.$q.localStorage.clear()
      this.loggedOut = true
      await this.$router.push({ path: '/login' })
      await this.reset()
    },
    async reset() {
      this.$DB.stopReplication()
      await Promise.all([
        this.$store.commit('audits/RESET_CURRENT_OTHER_CONTROL'),
        this.$DB.resetLocalDBs(this.$q.platform.is.capacitor),
        this.$patrimoine.resetLocalDBs(this.$q.platform.is.capacitor),
        this.$store.dispatch('reporter/reset'),
        NotificationService.clearNotifications()
      ])
      this.loggedOut = false
      this.notificationsLoading = false
    },
    processQueue() {
      if (!this.isOnline) return
      this.$cron.start('syncTmpItemsJob')
      this.$cron.start('syncUserPlacesJob')
    },
    pauseQueue() {
      this.$cron.stop('syncTmpItemsJob')
      this.$cron.stop('syncUserPlacesJob')
    },
    startSync() {
      var token = this.$store.getters['reporter/token']
      if (token && token.length) {
        this.$helpers
          .isTokenValid(token)
          .then(() => {
            this.watchLocalIssuesConflicts()
            this.$DB.initDBSync(
              this.$store,
              this.$store.getters['company/attributes'],
              this.$store.getters['reporter/attributes']
            )
            this.loadNotifications()
            eventBus.$emit('processQueue')
          })
          .then(() => {
            this.$store.dispatch('reporter/fetchProviders')
          })
          .catch(() => {
            // NOTE: do nothing
          })
      }
    },
    watchLocalIssuesConflicts() {
      var self = this
      if (this.changesListener) {
        this.changesListener.cancel()
      }
      this.changesListener = self.$DB
        .localIssues()
        .changes({
          since: 'now',
          live: true,
          include_docs: true
        })
        .on('change', (change) => {
          if (!('deleted' in change) || change.deleted === false) {
            self.$DB
              .localIssues()
              .get(change.doc._id, { conflicts: true })
              .then((localDoc) => {
                if (!localDoc) return
                // NOTE: Gestion des conflits en se basant sur l'attribut meta.updatedAt
                if ('_conflicts' in localDoc) {
                  let lastUpdatedAt = change.doc.meta.updatedAt
                  for (let conflict of localDoc._conflicts) {
                    self.$DB
                      .localIssues()
                      .get(change.doc._id, { _rev: conflict })
                      .then((conflictDoc) => {
                        if (!conflictDoc) return
                        if (
                          new Date(lastUpdatedAt) >
                          new Date(conflictDoc.meta.updatedAt)
                        ) {
                          self.$DB
                            .localIssues()
                            .remove(change.doc._id, conflict)
                            .catch((err) => {
                              console.error('error removing localIssues', {
                                err,
                                id: change.doc._id,
                                conflict
                              })
                              // NOTE: do nothing, the issue is already deleted
                            })
                        } else {
                          self.$DB
                            .localIssues()
                            .remove(change.doc._id, change.doc._rev)
                            .catch((err) => {
                              console.error('error removing localIssues', {
                                err,
                                id: change.doc._id,
                                rev: change.doc._rev
                              })
                              // NOTE: do nothing, the issue is already deleted
                            })
                        }
                      })
                  }
                }
              })
              .then(() => {
                eventBus.$emit('localIssueChange', change)
              })
          }
        })
        .on('error', (err) => {
          console.error('error change', err)
        })
    },
    async loadNotifications() {
      if (!this.notificationsLoading && !this.loggedOut) {
        this.notificationsLoading = true
        await this.clearNotificationsSeen()
        this.$SowellProxy.loadReporterNotifications().then(async (response) => {
          this.$store.dispatch(
            'reporter/setNotifications',
            response.data.notifications
          )
          this.notificationsLoading = false
        })
      }
    },
    async initOneSignal() {
      const oneSignal = window['plugins'].OneSignal
      oneSignal.initialize('3f6a1f2d-be99-48ff-b70f-17bfc70e4cc3')
      await oneSignal.Notifications.requestPermission(false)
      oneSignal.Notifications.addEventListener(
        'foregroundWillDisplay',
        (notificationReceivedEvent) => {
          if (this.$store.getters['reporter/token'].length) {
            this.$helpers
              .isTokenValid(this.$store.getters['reporter/token'])
              .then(() => {
                this.loadNotifications()
                // User is connected
                if (
                  !notificationReceivedEvent.notification.additionalData?.type
                ) {
                  this.$q.notify({
                    type: 'info',
                    position: 'top',
                    detail: notificationReceivedEvent.notification.body,
                    message: notificationReceivedEvent.notification.title
                  })
                } else {
                  const data =
                    notificationReceivedEvent.notification.additionalData
                  switch (data.type) {
                    case 'issue_status_changed': {
                      this.$q.notify({
                        type: 'info',
                        position: 'top',
                        detail: this.$t('notification.issue', {
                          issue: data.issue
                        }),
                        message: this.$t('notification.issue', {
                          issue: data.issue
                        }),
                        actions: [
                          {
                            label: 'Voir',
                            icon: 'open_in_new',
                            handler: () => {
                              if (
                                this.$route.name === 'issueDetail' ||
                                this.$route.name === 'issuesList'
                              ) {
                                eventBus.$emit('openModal', data.issue)
                              } else {
                                this.$router.push({
                                  path: '/history/' + data.issue
                                })
                              }
                            }
                          }
                        ]
                      })
                      break
                    }
                    case 'issue_talk_added': {
                      this.$q
                        .dialog({
                          title: this.$t('notification.talk.title'),
                          message: this.$t('notification.talk.message', {
                            issue: data.issue
                          }),
                          // optional
                          color: 'primary',
                          // optional; stacks button vertically instead of horizontally (default)
                          stackButtons: true,
                          // optional; we want an "OK" button
                          ok: this.$t('notification.talk.ok'), // takes i18n value, or String for "OK" button label
                          // optional; we want a "Cancel" button
                          cancel: this.$t('notification.talk.cancel') // takes i18n value, or String for "Cancel" button label
                        })
                        .onOk(() => {
                          if (
                            this.$route.name === 'issueDetail' ||
                            this.$route.name === 'issuesList'
                          ) {
                            eventBus.$emit('openModal', data.issue)
                          } else {
                            this.$router.push({
                              path: '/history/' + data.issue
                            })
                          }
                        })
                      break
                    }
                    case 'issue_created': {
                      this.$q
                        .dialog({
                          title: this.$t('notification.issueCreated.title'),
                          message: this.$t('notification.issueCreated.title', {
                            issue: data.issue
                          }),
                          // optional
                          color: 'primary',
                          // optional; stacks button vertically instead of horizontally (default)
                          stackButtons: true,
                          // optional; we want an "OK" button
                          ok: this.$t('notification.issueCreated.ok'), // takes i18n value, or String for "OK" button label
                          // optional; we want a "Cancel" button
                          cancel: this.$t('notification.issueCreated.cancel') // takes i18n value, or String for "Cancel" button label
                        })
                        .onOk(() => {
                          if (
                            this.$route.name === 'issueDetail' ||
                            this.$route.name === 'issuesList'
                          ) {
                            eventBus.$emit('openModal', data.issue)
                          } else {
                            this.$router.push({
                              path: '/history/' + data.issue
                            })
                          }
                        })
                      break
                    }
                    default: {
                      this.$q.notify({
                        type: 'info',
                        position: 'top',
                        detail: data.type,
                        message: this.$t('notification.default')
                      })
                    }
                  }
                }
              })
              .catch(() => {
                // NOTE: do nothing
              })
          }
        }
      )
      oneSignal.Notifications.addEventListener('click', (jsonData) => {
        let data = jsonData.notification.additionalData
        if (this.$store.getters['reporter/token'].length) {
          this.$helpers
            .isTokenValid(this.$store.getters['reporter/token'])
            .then(() => {
              // User is connected
              if (
                'type' in data &&
                (data.type === 'issue_status_changed' ||
                  data.type === 'issue_talk_added' ||
                  data.type === 'issue_created')
              ) {
                this.$router.push({ path: '/history/' + data.issue })
              }
            })
            .catch(() => {
              // NOTE: do nothing
            })
        }
      })
    },
    handleOnlineEvent() {
      this.isOnline = true
    },
    handleOfflineEvent() {
      this.isOnline = false
    },
    async clearNotificationsSeen() {
      const notificationsSeen =
        this.$store.getters['reporter/notificationsSeen']
      if (notificationsSeen.length) {
        try {
          await this.$SowellProxy.clearReporterNotifications({
            notifications: notificationsSeen.map((notificationSeen) => {
              return {
                notification_id: notificationSeen.id,
                notification_type: notificationSeen.type,
                notification_action: notificationSeen.action
              }
            })
          })
          if (this.$q.platform.is.capacitor && this.$q.appVisible) {
            window['plugins'].OneSignal.Notifications.clearAll()
          }
          this.$store.commit('reporter/CLEAR_NOTIFICATIONS_SEEN')
        } catch (e) {
          this.$store.dispatch(
            'reporter/setNotificationsSeen',
            notificationsSeen
          )
        }
      }
    },
    async getGeolocation() {
      if (!this.$q.platform.is.capacitor) {
        return
      }
      const newPosition = await getCurrentPosition(this.$q.dialog)
      if (newPosition) {
        try {
          const { latitude, longitude } = newPosition
          sessionStorage.setItem(
            'latestKnownPosition',
            `${longitude},${latitude}`
          )
        } catch (error) {
          sessionStorage.removeItem('latestKnownPosition')
        }
        eventBus.$emit('newPosition', newPosition)
      }
    },
    async updateGeolocation() {
      if (!this.$q.platform.is.capacitor) {
        return
      }
      const newPosition = await getCurrentPosition(this.$q.dialog)
      try {
        if (newPosition) {
          const { longitude, latitude } = newPosition
          sessionStorage.setItem(
            'latestKnownPosition',
            `${longitude},${latitude}`
          )
        }
      } catch (error) {
        console.error('Could not get new position')
      } finally {
        const hasKnownPosition =
          sessionStorage.getItem('latestKnownPosition') !== null
        if (!hasKnownPosition) {
          this.position = { longitude: null, latitude: null }
        } else {
          const latestKnownPosition = sessionStorage.getItem(
            'latestKnownPosition'
          )
          const [longitude, latitude] = latestKnownPosition.split(',')
          this.position = { longitude, latitude }
        }
        eventBus.$emit('newPosition', this.position)
      }
    },
    emitUpdatePosition() {
      eventBus.$emit('updatePosition')
    }
  },
  async beforeUnmount() {
    if (this.changesListener) {
      this.changesListener.cancel()
    }
    window.removeEventListener('offline', this.handleOfflineEvent)
    window.removeEventListener('online', this.handleOnlineEvent)
    const { App: CapacitorApp } = await import('Capacitor/@capacitor/app')
    CapacitorApp.removeAllListeners()
  }
}
</script>
